import { SendEventOnClick, SendEventOnView } from "~/components/Analytics"
import Icon from "~/components/ui/Icon"
import Slider from "~/components/ui/Slider"
import SliderJS from "~/components/ui/SliderJS"
import { useId } from "react"
import type { ImageWidget } from "~/types/widgets"
import type { SelectPromotionParams } from "@decocms/apps-commerce/types"
import { Picture, Source } from "~/components/ui/Picture"
import { isSchedulingActive, type Scheduling } from "~/sdk/contentScheduling"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import Image from "~/components/ui/Image"

/**
 * @titleBy alt
 */
export interface Banner {
    /**
     * @title Imagem desktop
     */
    desktop: ImageWidget

    /**
     * @title Imagem mobile
     */
    mobile: ImageWidget

    /**
     * @title Texto alternativo
     */
    alt: string

    /**
     * @title Link
     */
    href?: string

    /**
     * @title Agendamento
     * @description Deixe este campo vazio para manter sempre visível
     */
    scheduling?: Scheduling

    /**
     * @title Parâmetros do GA4
     */
    ga4?: SelectPromotionParams
}

export interface Props {
    /**
     * @title Imagens
     */
    banners?: Banner[]

    /**
     * @title Intervalo de tempo
     * @description tempo (em segundos) para as imagens trocarem automaticamente
     */
    interval?: number
}

const IMAGE_REDUCTION_FACTOR = 0.75

export const loader = (props: Props) => {
    if (!props.banners?.length) {
        return { ...props, banners: [] }
    }

    const banners = props.banners
        .filter(({ scheduling }) => isSchedulingActive(scheduling))
        .map(({ scheduling: _scheduling, ...banner }) => banner)

    return { ...props, banners }
}

function BannerItem(
    { banner, lcp }: {
        banner: Omit<Banner, 'start' | 'end'>
        lcp: boolean
    },
) {
    const id = useId()
    const { alt, mobile, desktop, href, ga4 } = banner

    const analyticsParams = {
        promotion_id: ga4?.promotion_id ?? 'banner_carousel',
        promotion_name: ga4?.promotion_name ?? alt ?? 'Carrosel de banners',
        creative_name: ga4?.creative_name,
        creative_slot: ga4?.creative_slot,
        view: {
            id: ga4?.promotion_id ?? 'banner_carousel',
            name: ga4?.promotion_name ?? alt ?? 'Carrosel de banners',
        },
    }

    const viewPromotionEvent = toAnalytics({
        type: 'view_promotion',
        data: analyticsParams,
    })

    const selectPromotionEvent = toAnalytics({
        type: 'select_promotion',
        data: analyticsParams,
    })

    const widthDesktop = 1920 * IMAGE_REDUCTION_FACTOR
    const heightDesktop = 450 * IMAGE_REDUCTION_FACTOR

    return (
        <a id={id} href={href} aria-label={alt} className='relative overflow-y-hidden w-full'>
            <Picture preload={lcp} className='flex justify-center'>
                {/*
                  * Ambos os <source> saem sempre no HTML e quem escolhe é o
                  * browser, pela media query. Antes o ramo era decidido no
                  * servidor por `isMobile` (User-Agent) — o que quebrava
                  * assim que a marcação era cacheada, já que o cache de
                  * section é chaveado só por component + props (sem UA):
                  * o primeiro visitante definia o ramo de todo mundo e o
                  * desktop recebia o banner mobile esticado no
                  * `lg:aspect-[1920/450]`. O breakpoint 1023/1024 casa com o
                  * `lg:` do aspect no <img> abaixo.
                  */}
                <Source
                    fetchPriority={lcp ? 'high' : 'auto'}
                    src={mobile}
                    media='(max-width: 1023px)'
                    width={768 * IMAGE_REDUCTION_FACTOR}
                    height={500 * IMAGE_REDUCTION_FACTOR}
                />

                <Source
                    src={desktop}
                    fetchPriority={lcp ? 'high' : 'auto'}
                    media='(min-width: 1024px)'
                    width={1920 * IMAGE_REDUCTION_FACTOR}
                    height={450 * IMAGE_REDUCTION_FACTOR}
                />

                <Image
                    className='w-full aspect-[768/500] lg:aspect-[1920/450]'
                    src={desktop}
                    alt={alt}
                    fetchPriority={lcp ? 'high' : 'auto'}
                    loading={lcp ? 'eager' : 'lazy'}
                    preload={lcp}
                    width={widthDesktop}
                    height={heightDesktop}
                />
            </Picture>

            <SendEventOnView id={id} event={viewPromotionEvent} />
            <SendEventOnClick id={id} event={selectPromotionEvent} />
        </a>
    )
}

function Dots({ banners, interval = 0 }: ReturnType<typeof loader>) {
    return (
        <>
            <ul className='flex justify-center col-span-full gap-4 z-[0] row-start-4'>
                {banners?.map((_, index) => (
                    <li key={index} className='carousel-item'>
                        <Slider.Dot index={index}>
                            <div className='py-5'>
                                <div
                                    className='bg-[#b3adaa] group-disabled:bg-[#d92c50] w-[1.5625rem] h-[0.375rem] rounded-full'
                                    style={{
                                        animationDuration: `${interval}s`,
                                    }}
                                />
                            </div>
                        </Slider.Dot>
                    </li>
                ))}
            </ul>
        </>
    )
}

function Buttons() {
    return (
        <>
            <div className='flex items-center justify-center z-[0] col-start-1 relative row-start-2 top-1/2'>
                <Slider.PrevButton className='bg-[#E1E1E1CC] flex items-center justify-center w-10 h-10 lg:h-[3.75rem] lg:w-[3.75rem] rounded-full shrink-0'>
                    <Icon
                        className='w-6 h-6 lg:h-[3rem] lg:w-[3rem] object-contain text-black stroke-0 hover:text-[#E5554F] transition-colors'
                        id='BannerChevronLeft'
                        strokeWidth={0}
                    />
                </Slider.PrevButton>
            </div>
            <div className='flex items-center justify-center z-[0] col-start-3 relative row-start-2 top-1/2'>
                <Slider.NextButton className='bg-[#E1E1E1CC] flex items-center justify-center w-10 h-10 lg:h-[3.75rem] lg:w-[3.75rem] rounded-full shrink-0'>
                    <Icon
                        className='w-6 h-6 lg:h-[3rem] lg:w-[3rem] object-contain text-black stroke-0 hover:text-[#E5554F] transition-colors'
                        size={48}
                        id='BannerChevronRight'
                        strokeWidth={0}
                    />
                </Slider.NextButton>
            </div>
        </>
    )
}

function BannerCarousel({ banners, interval = 0 }: ReturnType<typeof loader>) {
    const id = useId()

    return (
        <div
            id={id}
            className='grid grid-cols-[48px_1fr_48px] sm:grid-cols-[120px_1fr_120px] grid-rows-[1fr_48px_1fr_64px] h-full'
        >
            <Slider className='flex flex-nowrap snap-x snap-mandatory overflow-x-scroll scroll-smooth scrollbar-none items-stretch w-full col-span-full row-span-full'>
                {banners?.map((banner, index) => (
                    <Slider.Item
                        key={banner.href ?? `banner-${index}`}
                        className='flex-none w-full snap-start'
                        index={index}
                    >
                        <BannerItem banner={banner} lcp={index === 0} />
                    </Slider.Item>
                ))}
            </Slider>

            <Buttons />

            <Dots banners={banners} interval={interval} />

            <SliderJS rootId={id} interval={interval * 1000} infinite />
        </div>
    )
}

export default BannerCarousel
